import * as React from "react";
import styles from "./Header.module.css";
import cx from "classnames";
import { useQuery, useSelector, useRedux, useStateModal } from "hooks";
import listIcon from "assets/images/68.svg";
import boardIcon from "assets/images/69.svg";
import dailyProductionIcon from "assets/images/204.svg";
import { useParams, Link } from "react-router-dom";
import { dateFns, queryString } from "utilities";
import { AvailableViewKeys } from "ducks/ui";
import { ToasterDropdown } from "./toasterDropdown";
import { Button } from "../buttonLegacy";
import { DailyStatistics } from "./dailyStatistics";
import { FLAVOR } from "CONSTANTS";
import { DevModeBar } from "components/utils/devModeBar";
import { HeaderUserDropdown } from "../headerUserDropdown/HeaderUserDropdown";
import { ModuleMenuDropdown } from "../pageHeader/components/ModuleMenuDropdown";

interface Props {
  title: string | undefined | ((arg: { styles: { title: string } }) => React.ReactNode);
  subtitle?: React.ReactNode;
  viewSwitch?: boolean;
  tabs?: { label: string; name: string; path?: string; exactPath?: boolean }[];
  routeRoot?:
    | "orders"
    | "client/orders"
    | "products"
    | "routes"
    | "shipment"
    | "order-groups"
    | "users"
    | "complaints"
    | "manufacturer-orders"
    | "self-production-orders"
    | "warehouse"
    | "settings"
    | "upholsteries"
    | "reservations"
    | "upholstery-production-orders"
    | "productSets"
    | "indexes"
    | "statuses"
    | "labels"
    | "fiscalPrinters"
    | "trading-documents"
    | "finances/trading-documents"
    | "sellers"
    | "business-entities"
    | "vatRates"
    | "exchangeRates"
    | "manufacturing-order-groups"
    | "email-templates"
    | "production-plans"
    | "warehouse-locations"
    | "signature-schemas"
    | "bank-accounts"
    | "finances/bank-accounts"
    | "finances/bank-accounts/transactions"
    | "bank-accounts/transactions"
    | string;
  viewType?: AvailableViewKeys;
  urlSpan?: string;
  customColumn?: React.ReactNode;
  customFooter?: React.ReactNode;
  showDailyStatistics?: boolean;
}

export const Header = ({
  title,
  subtitle,
  viewSwitch = false,
  tabs,
  routeRoot,
  viewType,
  customColumn,
  customFooter,
  showDailyStatistics = false,
  urlSpan = "list",
}: Props) => {
  const params = useParams<{ tab: string; navbarTab: string }>();
  const { query } = useQuery();
  const modal = useStateModal();
  const activeViewType = useSelector(state => viewType && state.ui.viewTypes[viewType]);
  const [dispatch, { ui }] = useRedux();
  const showTabs = activeViewType === undefined || activeViewType === "list";
  const hasDailyProductionService = useSelector(
    state => state.partials.configuration.hasDailyProductionService,
  );

  function renderTitle() {
    if (!title) return null;
    if (typeof title === "string") {
      return <h1 className={styles.title}>{title}</h1>;
    }
    if (typeof title === "function") {
      return title({ styles: { title: styles.title } });
    }
    return title;
  }

  function getPath(tab: NonNullable<Props["tabs"]>[number]) {
    const search = queryString.stringify({ ...query, page: "" });
    if (tab.exactPath) {
      return String(tab.path);
    }
    if (tab.path) {
      return `${tab.path}/${tab.name}${search}`;
    }
    return `/${routeRoot}/${urlSpan}/${tab.name}${
      params.navbarTab ? "/" + params.navbarTab : ""
    }${search}`;
  }

  return (
    <header>
      <div className={styles.header}>
        <div className="d-flex">
          <div className={cx("d-flex flex-column", styles.headerLeft)}>
            <div className="d-flex align-items-center">
              <div className="mt-2">{renderTitle()}</div>
              {viewSwitch && activeViewType && (
                <div className={styles.viewTabs}>
                  <button
                    className={cx("mr-2", { [styles.viewTabActive]: activeViewType === "list" })}
                    onClick={() => dispatch(ui.toggleViewType({ type: "orders", value: "list" }))}
                  >
                    <img src={listIcon} alt="Lista" />
                  </button>
                  <button
                    className={cx({ [styles.viewTabActive]: activeViewType === "board" })}
                    onClick={() => dispatch(ui.toggleViewType({ type: "orders", value: "board" }))}
                  >
                    <img src={boardIcon} alt="Tablica" />
                  </button>
                </div>
              )}
            </div>
            {subtitle && <div className={styles["subtitle"]}>{subtitle}</div>}
            <div className={cx("d-flex", styles.navTabs)}>
              {tabs &&
                showTabs &&
                tabs.map(tab => (
                  <Link
                    key={tab.name}
                    to={getPath(tab)}
                    className={cx(styles.navTab, {
                      [styles.navTabActive]: params.tab === tab.name,
                    })}
                  >
                    {tab.label}
                  </Link>
                ))}
            </div>
            {customFooter}
          </div>
          {customColumn}
        </div>
        <div className="d-flex justify-content-end align-items-center">
          {FLAVOR !== "b2b" && showDailyStatistics && hasDailyProductionService && (
            <div className="border-right pr-2 mr-2 d-flex align-items-center justify-content-center">
              <Button
                kind="secondary-stroke"
                size="small"
                onClick={() => {
                  const currentDate = dateFns.format(new Date(), "yyyy-MM-dd");
                  modal.open(currentDate);
                }}
              >
                <img src={dailyProductionIcon} alt="Helpdesk" className="mr-2" />
                Dzienna produkcja
              </Button>
              <DailyStatistics stateModal={modal} />
            </div>
          )}
          <DevModeBar />

          <ModuleMenuDropdown />

          <ToasterDropdown />

          <HeaderUserDropdown />
        </div>
      </div>
    </header>
  );
};
